'use client';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { ComponentProps, ReactNode } from 'react';
import { ProductCard } from '@/entities/product/ui/product-card';
import { RecommendedProductsCarousel } from '@/entities/product/ui/recommended-products-carousel';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';

import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';

import './top-up-order-modal.scss';
import { useCartStore, getPriceValueTranslation } from '@/entities/cart';

type Props = ModalProps & {
    children?: ReactNode;
};

type TitleProps = ComponentProps<typeof Layout>;
const TopUpOrderModalTitle = ({ media, ...props }: TitleProps) => {
    const t = useTranslations();
    return (
        <Layout element={Modal.Title} media={media} className="top-up-order-modal__title" {...props}>
            {t('TOP_UP_ORDER_MODAL.TITLE')}
        </Layout>
    );
};

type DescriptionProps = ComponentProps<typeof Layout>;
const TopUpOrderModalDescription = ({ media, ...props }: DescriptionProps) => {
    const t = useTranslations();
    const [selectedCurrency] = useSelectedCurrency();
    const topUpFee = useCartStore((s) => s.topUpFee);
    return (
        <Layout
            element={Modal.Description}
            media={media}
            className="top-up-order-modal__description"
            {...props}
        >
            {t('TOP_UP_ORDER_MODAL.DESCRIPTION', {
                amount: getPriceValueTranslation({ amount: topUpFee, currency: selectedCurrency, t }),
            })}
        </Layout>
    );
};

const TopUpOrderModalCarousel = () => {
    const [selectedCurrency] = useSelectedCurrency();
    return (
        <RecommendedProductsCarousel className="top-up-order-modal__carousel">
            {[1, 2, 3, 4].map((_, index) => (
                <ProductCard
                    key={index}
                    id={`${index + 1}`}
                    hash={`${index + 1}`}
                    slug=""
                    imageUrl="https://admin.cdn.dev.ordermage.com/item/019dd8ca-c9d6-7d2d-8cb1-3daf321e372d.webp"
                    name={{ CS: 'Idk', EN: 'Idk' }}
                    priceValues={[{ amount: 100, currency: selectedCurrency }]}
                    className="top-up-order-modal__product-card"
                />
            ))}
        </RecommendedProductsCarousel>
    );
};

type LinkProps = ComponentProps<typeof Layout>;
const TopUpOrderModalLink = ({ media, ...props }: LinkProps) => {
    const t = useTranslations();
    return (
        <Layout element={Link} media={media} href="#" className="top-up-order-modal__link" {...props}>
            {t('TOP_UP_ORDER_MODAL.EXPLORE_LINK')}
        </Layout>
    );
};

export const TopUpOrderModalWrapper = ({ children, ...props }: Props) => {
    return (
        <Modal
            {...props}
            wrapperProps={{
                containerProps: { className: 'top-up-order-modal__wrapper' },
                'data-testid': 'top-up-order-modal',
            }}
        >
            {children}
        </Modal>
    );
};

/** Provider that only provides context and renders children (Modal prefab). Use with prefab: "Modal" as child. */
export const TopUpOrderModalProvider = ({ children }: Props) => {
    return <>{children}</>;
};

export {
    TopUpOrderModalWrapper as Wrapper,
    TopUpOrderModalProvider as Provider,
    TopUpOrderModalTitle as Title,
    TopUpOrderModalDescription as Description,
    TopUpOrderModalCarousel as Carousel,
    TopUpOrderModalLink as Link,
};

export { TopUpOrderModalWrapper as TopUpOrderModal };
